/* Qué columna muestra cada listado.
 *
 * ── `derivada: true` ES LA COLUMNA QUE NO EXISTE EN LA TABLA ─────────────
 *
 * Varias de estas columnas no son campos: las arma el service al enriquecer
 * la fila —`nombreEmpresa` resuelve un CUIT contra el catálogo de empresas,
 * `tractorFull` pega interno y dominio, `lonaTexto` traduce un booleano a
 * «Con lona»—. Se ven igual que las otras y no se pueden escribir: PostgREST
 * rechaza la fila entera con un `PGRST204` que ni siquiera nombra la pantalla.
 *
 * Sin la marca, el lápiz del padrón les ofrecía un input que sólo podía
 * contestar «no se pudo guardar». La marca va acá porque éste es el archivo
 * que se toca el día que se agrega una columna, y `celdaEditable.sePuedeTocar`
 * la lee.
 *
 * Y NO CONVIERTE ESTO EN UNA LISTA BLANCA: lo que se marca es lo que no
 * existe, así que una columna real que alguien agregue mañana nace editable
 * —que es el error barato— y una derivada sin marcar falla ruidoso, como
 * fallaba hasta hoy.
 */
export const columnas = {
  eventos: [
    { key: "id", label: "Nro Evento", filtroEspecial: true },
    { key: "fecha", label: "Fecha" },
    { key: "tipo", label: "Evento" },
    { key: "area", label: "Sector", filtroEspecial: true },
    { key: "nombrePersona", label: "Persona", derivada: true },
    { key: "tractorFull", label: "Tractor", filtroEspecial: true, derivada: true },
    { key: "furgonFull", label: "Carga / Furgón", filtroEspecial: true, derivada: true },
    { key: "vehiculo", label: "Otro" },
    { key: "detalle", label: "Detalle" },
    { key: "usuario", label: "Usuario" }
  ],
  asignaciones: [
    { key: "id", label: "Asignación n°", filtroEspecial: true },
    { key: "fecha", label: "Fecha" },
    { key: "nombrePersona", label: "Persona", derivada: true },
    { key: "nombreArticulo", label: "Articulo / herramienta", derivada: true },
    { key: "cantidadFull", label: "Cantidad", derivada: true },
    { key: "usuario", label: "Cargado por" }
  ],
  taller: [
    { key: "id", label: "Nro Evento", filtroEspecial: true },
    { key: "fecha", label: "Fecha" },
    { key: "tipo", label: "Evento" },
    { key: "area", label: "Sector", filtroEspecial: true },
    { key: "nombrePersona", label: "Persona", derivada: true },
    { key: "nombreMecanico", label: "Mecanico", derivada: true },
    { key: "nombreProveedor", label: "Servicio", filtroEspecial: true, derivada: true },
    { key: "kilometraje", label: "KM" },
    { key: "furgonFull", label: "Furgon", filtroEspecial: true, derivada: true },
    { key: "tractorFull", label: "Tractor", filtroEspecial: true, derivada: true },
    { key: "detalle", label: "Detalle" },
    { key: "nombreSucursal", label: "Sucursal", derivada: true },
    { key: "usuario", label: "Usuario" }
  ],
  tractores: [
    { key: "id", label: "Interno", filtroEspecial: true },
    { key: "dominio", label: "Dominio" },
    /* EL RTO, EN EL PADRÓN. Antes vivía en una pantalla aparte —«RTO de
     * tractores»— que era el mismo listado con otra columna. Ahora es una
     * columna más de acá, con su semáforo, y `filtroEspecial` la vuelve el
     * filtro que contesta la pregunta del lunes: se elige «Vencido» y queda la
     * lista de lo que hay que renovar. Es `derivada` porque no es un campo del
     * padrón: la pega `Padron.jsx` cruzando `rto_tractores()` por interno, así
     * que el lápiz no le ofrece un input que sólo sabría fallar. */
    { key: "rtoTexto", label: "RTO", filtroEspecial: true, derivada: true },
    { key: "nombreEmpresa", label: "Empresa", derivada: true },
    { key: "marca", label: "Marca" },
    { key: "modelo", label: "Modelo" },
    { key: "nombreResponsable", label: "Responsable", derivada: true },
    { key: "nombreSatelital", label: "Satelital", derivada: true },
    { key: "detalle", label: "Detalle" },
    // Arranca en Activo, igual que personas: de 657 tractores trabajan 400,
    // y las otras 257 son bajas, vendidos y sin dato. Una lista donde cuatro
    // de cada diez filas son unidades que ya no ruedan obliga a filtrar a mano
    // todas las veces — y el que se olvida de hacerlo carga un viaje a un
    // camión vendido. La búsqueda le gana igual al filtro, así que a un dado
    // de baja se llega escribiendo su patente.
    { key: "estadoReal", label: "Estado", filtroEspecial: true,
      filtroPorDefecto: "Activo" },
    { key: "usuario", label: "Cargó" },
  ],
  furgones: [
    /* SE MUESTRA "Interno" Y LA CLAVE SIGUE SIENDO `id`.
     *
     * Adentro de la empresa a esto se le dice interno —el 774, el F774— y
     * "ID" no lo dice nadie. Pero el campo se llama `id` porque así viene de
     * Firestore, que es de donde se sincroniza el padrón: renombrarlo del lado
     * de los datos rompería la sincronización por una etiqueta.
     *
     * Es exactamente lo que ya hace `tractores` dos líneas más arriba. */
    { key: "id", label: "Interno", filtroEspecial: true },
    { key: "dominio", label: "Dominio" },
    /* EL RTO DEL ACOPLADO, en el padrón, igual que en tractores. Reemplaza a la
     * pantalla «RTO de acoplados», que era este mismo listado con la columna de
     * vencimiento. La pega `Padron.jsx` cruzando `rto_furgones()` por interno. */
    { key: "rtoTexto", label: "RTO", filtroEspecial: true, derivada: true },
    { key: "nombreEmpresa", label: "Empresa", derivada: true },
    /* LONA, con filtro. La columna sola no alcanza: son 679 filas sin el dato
     * y sin poder filtrar por «Sin dato» no hay forma de trabajar la lista de
     * a poco — que es la única manera en que esto se va a llenar. El valor
     * derivado lo arma el service, para que la tabla no traduzca por su
     * cuenta. */
    { key: "lonaTexto", label: "Lona", filtroEspecial: true, derivada: true },
    /* Playo / furgón / portacontenedor. Nace con 679 «Sin dato» y con
     * filtro, que es lo que deja ver de una cuántos faltan. */
    { key: "tipoAcopladoTexto", label: "Carrocería", filtroEspecial: true, derivada: true },
    /* LO QUE CARGA FEDERICO, y son DOS columnas distintas de la de arriba.
     *
     * «Carrocería» es la de Ricardo (`tipo_acoplado`) y «Tipo taller» es la
     * clasificación del taller (`tipo_taller`). Con las dos rotuladas «Tipo»
     * no habría forma de saber cuál se está mirando — y ésa es exactamente la
     * confusión que tuvo el botón de `/FurgonesApp` roto desde el primer día.
     *
     * Sin estas dos, el trabajo que Federico carga todos los días desde el
     * taller no se ve en ninguna pantalla compartida, y la única columna de
     * «Tipo» que había iba a quedar en «Sin dato» para siempre. */
    { key: "tipoTallerTexto", label: "Tipo taller", filtroEspecial: true, derivada: true },
    { key: "puertaTexto", label: "Puerta", filtroEspecial: true, derivada: true },
    { key: "detalle", label: "Detalle" },
    { key: "estadoReal", label: "Estado", filtroEspecial: true,
      filtroPorDefecto: "Activo" },
    { key: "usuario", label: "Cargó" },
  ],
  vehiculos: [
    { key: "id", label: "Dominio", filtroEspecial: true },
    { key: "marca", label: "Marca", filtroEspecial: true },
    { key: "modelo", label: "Modelo" },
    { key: "nombreResponsable", label: "Responsable", derivada: true },
    { key: "estadoReal", label: "Estado", filtroEspecial: true,
      filtroPorDefecto: "Activo" },
    { key: "usuario", label: "Cargó" },
  ],
  personas: [
    { key: "id", label: "DNI", filtroEspecial: true },
    { key: "legajo", label: "Legajo", filtroEspecial: true },
    { key: "nombreCompleto", label: "Persona", derivada: true },
    { key: "nombreEmpresa", label: "Empresa", derivada: true },
    { key: "puestoCompleto", label: "Puesto", derivada: true },
    // `estadoReal` y no `estado`: el crudo trae "1"/"true"/"0"/"false"/""/null
    // y el filtro terminaba ofreciendo "true" y "false" como si fueran
    // categorías. Arranca en Activo porque de 1.000 personas se trabaja con
    // 191: una lista donde el 80% son bajas es una lista que no se usa.
    { key: "estadoReal", label: "Estado", filtroEspecial: true,
      filtroPorDefecto: "Activo" },
    { key: "usuario", label: "Cargó" },
  ],
  stock: [
    { key: "id", label: "Codigo", filtroEspecial: true },
    { key: "tipo", label: "Tipo", filtroEspecial: true },
    { key: "descripcion", label: "Descripcion" },
    { key: "marca", label: "Marca", filtroEspecial: true },
    { key: "nombreProveedor", label: "Proveedor", filtroEspecial: true, derivada: true },
    { key: "cantidad", label: "Cantidad" },
    { key: "unidad", label: " " },
    //{key: "deposito", label: "Deposito", filtroEspecial: true},
    { key: "usuario", label: "Cargó" },
  ],
  proveedores: [
    { key: "id", label: "Codigo", filtroEspecial: true },
    { key: "cuit", label: "Cuit", filtroEspecial: true },
    { key: "nombre", label: "Razon social" },
    { key: "marca", label: "Nombre", filtroEspecial: true },
    { key: "detalle", label: "Detalle" },
    // Pedido de Nico (25/09): teléfono y sitio web de cada proveedor, para que
    // Andrés Millet los vaya cargando. Se editan en el padrón (/padron/proveedores,
    // celda por celda) y se guardan en Firestore, que no pide esquema.
    { key: "telefono", label: "Teléfono" },
    { key: "web", label: "Sitio web" },
    { key: "usuario", label: "Cargó" },
  ],
  depositos: [
    // lo que se lista es el atributo "stock" de cada depósito particular
    { key: "id", label: "Codigo", filtroEspecial: true },
    { key: "tipo", label: "Tipo", filtroEspecial: true, derivada: true },
    { key: "descripcion", label: "Descripcion", filtroEspecial: true, derivada: true },
    { key: "marca", label: "Marca", filtroEspecial: true, derivada: true },
    { key: "nombreProveedor", label: "Proveedor", filtroEspecial: true, derivada: true },
    { key: "cantidad", label: "Cantidad", derivada: true },
    { key: "unidad", label: "Un. de medida", derivada: true }
  ]
};